import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '../internal/tone';

export interface BigButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'> {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  /** `xl` é o botão do fundo da tela: 76 px, confortável para o polegar. */
  size?: 'md' | 'lg' | 'xl';
  icon?: string;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
}

/**
 * Botão principal do aplicativo.
 *
 * É um <button> nativo: teclado, leitor de tela e clique vêm de graça e não se
 * partem. Em estado `loading` continua a anunciar-se como ocupado (aria-busy)
 * em vez de desaparecer — mudar a tela por baixo do dedo dela é confuso.
 */
export function BigButton({
  children,
  onClick,
  variant = 'primary',
  size = 'lg',
  icon,
  loading = false,
  disabled = false,
  fullWidth = false,
  className,
  ...rest
}: BigButtonProps) {
  return (
    <button
      type="button"
      className={cx(
        'flos-btn',
        `flos-btn--${variant}`,
        `flos-btn--${size}`,
        fullWidth && 'flos-btn--fullwidth',
        className,
      )}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <>
          <span className="flos-spinner__ring" aria-hidden="true" />
          <span>Carregando…</span>
        </>
      ) : (
        <>
          {icon ? (
            <span className="flos-btn__icon flos-icon" aria-hidden="true">
              {icon}
            </span>
          ) : null}
          <span>{children}</span>
        </>
      )}
    </button>
  );
}
